import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useAppState, useDispatch } from '../state/AppContext';
import { useAuth } from '../state/AuthContext';
import { SELECT_NODE } from '../state/actions';
import { useAudioPreview } from '../hooks/useAudioPreview';
import { findArtistTrack } from '../api/musicClient';
import { flushQueue as flushDeezerQueue } from '../api/deezerClient';
import PreviewPlayer from './PreviewPlayer';
import '../styles/galaxy.css';

function buildSequentialOrder(nodes) {
  const positioned = nodes.filter((n) => n.x != null && n.y != null);
  if (positioned.length === 0) return [];

  const cx = positioned.reduce((s, n) => s + n.x, 0) / positioned.length;
  const cy = positioned.reduce((s, n) => s + n.y, 0) / positioned.length;

  return [...positioned].sort((a, b) => {
    const angleA = Math.atan2(a.x - cx, cy - a.y);
    const angleB = Math.atan2(b.x - cx, cy - b.y);
    return angleA - angleB;
  });
}

function buildShuffleOrder(nodes) {
  const positioned = nodes.filter((n) => n.x != null && n.y != null);
  const shuffled = [...positioned];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

const MAX_CONSECUTIVE_SKIPS = 5;

function GalaxyPlayerController({ canvasRef, externalSelectedNode, onExternalSelectNode, portalSelector, hideRecsFilter, extraControls, externalFilter }) {
  const { selectedNode: reduxSelectedNode, galaxyData } = useAppState();
  const dispatch = useDispatch();
  const { user, favorites, discoveredArtists, dislikes, knownArtists } = useAuth();

  const externalMode = !!onExternalSelectNode;
  const selectedNode = externalMode ? externalSelectedNode : reduxSelectedNode;

  const [mode, setMode] = useState('sequential');
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isActive, setIsActive] = useState(false);
  const [currentNodeTrack, setCurrentNodeTrack] = useState(null);
  const [isLoadingTrack, setIsLoadingTrack] = useState(false);

  // Genre filter: empty Set = "All" (show all recs), non-empty = filter to those genres
  const [selectedGenres, setSelectedGenres] = useState(new Set());

  // Bump to force re-render after playlist refs are rebuilt (e.g. when filter
  // changes don't update currentIndex because it's already 0).
  const [, setPlaylistVersion] = useState(0);
  const bumpPlaylistVersion = useCallback(() => setPlaylistVersion((v) => v + 1), []);

  const trackCache = useRef(new Map());
  const sequentialOrder = useRef([]);
  const shuffleOrder = useRef([]);
  const navigationGenRef = useRef(0);
  const skipCountRef = useRef(0);
  const navigatingFromClickRef = useRef(false);
  const silentSyncRef = useRef(false);

  // Track current index imperatively so lock-screen handlers (which fire
  // while React effects are suspended on iOS) can compute the next index.
  const currentIndexRef = useRef(-1);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);

  // Refs for mode and isActive so the dislike effect reads fresh values
  const modeRef = useRef(mode);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  const isActiveRef = useRef(false);
  useEffect(() => { isActiveRef.current = isActive; }, [isActive]);

  // Ref for navigateToIndex — handlers are defined before it, so they
  // call through the ref to avoid a circular dependency.
  const navigateToIndexRef = useRef(null);

  // When a handler calls navigateToIndex directly, this flag prevents
  // the useEffect from triggering a duplicate navigation.
  const directNavRef = useRef(false);

  // Select node — dispatches to Redux or calls external handler
  const selectNode = useCallback((node) => {
    if (externalMode) {
      onExternalSelectNode(node);
    } else {
      dispatch({ type: SELECT_NODE, payload: node });
    }
  }, [externalMode, onExternalSelectNode, dispatch]);

  // Recommendations-only filter
  const favoriteNames = useMemo(
    () => new Set((favorites || []).map((f) => f.artistName)),
    [favorites]
  );
  const discoveredNames = useMemo(
    () => new Set((discoveredArtists || []).map((d) => d.artistName)),
    [discoveredArtists]
  );
  const dislikeNames = useMemo(
    () => new Set((dislikes || []).map((d) => d.artistName)),
    [dislikes]
  );
  const knownNames = useMemo(
    () => new Set((knownArtists || []).map((k) => k.artistName)),
    [knownArtists]
  );

  const filterNodes = useCallback((nodes) => {
    let filtered = nodes;
    // On the galaxy map, exclude seed artists — explore mode is for
    // discovering recommendations, not replaying the artists the user seeded.
    if (!externalMode) {
      filtered = filtered.filter((n) => n.type !== 'seed');
    }
    // Always exclude disliked artists on the galaxy map (non-external mode)
    if (user && dislikeNames.size > 0) {
      filtered = filtered.filter((n) => !dislikeNames.has(n.name));
    }
    // Filter to recs only in universe explore mode (skip when an external
    // filter like collision zone selection already controls which nodes appear).
    // Excludes any artist the user has marked: favorite, neutral, or discovered.
    if (user && !externalFilter) {
      filtered = filtered.filter((n) =>
        !favoriteNames.has(n.name) &&
        !discoveredNames.has(n.name) &&
        !knownNames.has(n.name)
      );
    }
    // Filter by selected genres if any are chosen
    if (selectedGenres.size > 0) {
      filtered = filtered.filter((n) => {
        const nodeGenres = n.genres || [];
        return nodeGenres.some((g) => selectedGenres.has(g));
      });
    }
    return filtered;
  }, [selectedGenres, user, favoriteNames, discoveredNames, dislikeNames, knownNames, externalFilter, externalMode]);

  const hasFilterableArtists = !!(user && (favoriteNames.size > 0 || discoveredNames.size > 0 || knownNames.size > 0));

  // Collect unique genres from all nodes for the genre selector.
  // The canvas builds its layout asynchronously, so poll briefly until nodes appear.
  const [availableGenres, setAvailableGenres] = useState([]);
  useEffect(() => {
    let frame;
    let attempts = 0;
    const poll = () => {
      const allNodes = canvasRef.current?.getNodes() || [];
      if (allNodes.length > 0 || attempts > 20) {
        const genreSet = new Set();
        for (const n of allNodes) {
          for (const g of (n.genres || [])) genreSet.add(g);
        }
        setAvailableGenres([...genreSet].sort());
        return;
      }
      attempts++;
      frame = requestAnimationFrame(poll);
    };
    poll();
    return () => cancelAnimationFrame(frame);
  }, [canvasRef, galaxyData]); // eslint-disable-line react-hooks/exhaustive-deps

  const getPlaylist = useCallback(() => {
    return mode === 'shuffle' ? shuffleOrder.current : sequentialOrder.current;
  }, [mode]);

  // Auto-advance / next / prev handlers.
  // These call navigateToIndex directly (via ref) so that lock-screen and
  // auto-advance playback works even when the app is backgrounded on iOS
  // (where React effects are suspended and would never trigger navigation).
  const handleAutoAdvance = useCallback(() => {
    const playlist = mode === 'shuffle' ? shuffleOrder.current : sequentialOrder.current;
    if (playlist.length === 0) return;
    const next = currentIndexRef.current + 1 < playlist.length ? currentIndexRef.current + 1 : 0;
    currentIndexRef.current = next;
    directNavRef.current = true;
    setCurrentIndex(next);
    navigateToIndexRef.current?.(next);
  }, [mode]);

  const handleNext = useCallback(() => {
    const playlist = getPlaylist();
    if (playlist.length === 0) return;
    const next = currentIndexRef.current + 1 < playlist.length ? currentIndexRef.current + 1 : 0;
    currentIndexRef.current = next;
    directNavRef.current = true;
    setCurrentIndex(next);
    navigateToIndexRef.current?.(next);
  }, [getPlaylist]);

  const handlePrev = useCallback(() => {
    const playlist = getPlaylist();
    if (playlist.length === 0) return;
    const next = currentIndexRef.current - 1 >= 0 ? currentIndexRef.current - 1 : playlist.length - 1;
    currentIndexRef.current = next;
    directNavRef.current = true;
    setCurrentIndex(next);
    navigateToIndexRef.current?.(next);
  }, [getPlaylist]);

  // When a preview URL expires, clear all stale cached data (URLs from
  // the same session are all similarly stale) and re-fetch the current
  // track from Deezer for a fresh preview URL.
  const handlePlaybackError = useCallback(async () => {
    trackCache.current.clear();

    const idx = currentIndexRef.current;
    const playlist = mode === 'shuffle' ? shuffleOrder.current : sequentialOrder.current;
    if (idx < 0 || idx >= playlist.length) return null;

    const node = playlist[idx];
    try {
      const fresh = await findArtistTrack(node.name, node.id, { priority: true });
      if (fresh?.previewUrl) {
        trackCache.current.set(node.id, fresh);
        setCurrentNodeTrack(fresh);
        return fresh;
      }
    } catch {}
    return null;
  }, [mode]);

  const { isPlaying, progress, play: audioPlay, pause: audioPause, toggle: audioToggle, seek: audioSeek, setScrubbing, prefetchArtwork } = useAudioPreview({ onEnded: handleAutoAdvance, onNext: handleNext, onPrev: handlePrev, onPlaybackError: handlePlaybackError, mediaSession: true });

  const eagerPrefetchedRef = useRef(false);

  // Clear playlists when galaxy data changes (will rebuild on play start)
  useEffect(() => {
    if (externalMode || !galaxyData) return;
    sequentialOrder.current = [];
    shuffleOrder.current = [];
    trackCache.current.clear();
    eagerPrefetchedRef.current = false;
  }, [galaxyData, externalMode]);

  // Eagerly prefetch the first track so explore feels instant on click.
  // Waits a short delay for the canvas to finish positioning nodes, then
  // builds the sequential playlist and fetches the first artist's track
  // into the cache — no playback is started.
  useEffect(() => {
    if (isActive || eagerPrefetchedRef.current) return;

    const timer = setTimeout(() => {
      const allNodes = canvasRef.current?.getNodes() || [];
      const nodes = filterNodes(allNodes);
      if (nodes.length === 0) return;

      // Build the sequential playlist so we know which node is first
      if (sequentialOrder.current.length === 0) {
        sequentialOrder.current = buildSequentialOrder(nodes);
      }
      const firstNode = sequentialOrder.current[0];
      if (!firstNode || trackCache.current.has(firstNode.id)) return;

      eagerPrefetchedRef.current = true;
      findArtistTrack(firstNode.name, firstNode.id, { priority: true })
        .then((track) => {
          if (track) trackCache.current.set(firstNode.id, track);
        })
        .catch(() => {});
    }, 500);

    return () => clearTimeout(timer);
  }, [isActive, canvasRef, filterNodes]);

  // Build playlists on demand from current (settled) node positions
  const ensurePlaylistsBuilt = useCallback(() => {
    const allNodes = canvasRef.current?.getNodes() || [];
    const nodes = filterNodes(allNodes);
    if (nodes.length === 0) return;
    if (sequentialOrder.current.length === 0) {
      sequentialOrder.current = buildSequentialOrder(nodes);
    }
    if (shuffleOrder.current.length === 0) {
      shuffleOrder.current = buildShuffleOrder(nodes);
    }
  }, [canvasRef, filterNodes]);

  // Rebuild playlists when genre filter changes during active playback
  const genreFilterMountRef = useRef(true);
  useEffect(() => {
    if (genreFilterMountRef.current) {
      genreFilterMountRef.current = false;
      return;
    }
    sequentialOrder.current = [];
    shuffleOrder.current = [];
    if (!isActive) return;
    ensurePlaylistsBuilt();
    setCurrentIndex(0);
    bumpPlaylistVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGenres]);

  // Rebuild playlists when external filter (e.g. collision zone selection) changes
  const externalFilterMountRef = useRef(true);
  useEffect(() => {
    if (externalFilterMountRef.current) {
      externalFilterMountRef.current = false;
      return;
    }
    if (!externalFilter) return;
    sequentialOrder.current = [];
    shuffleOrder.current = [];
    if (!isActive) return;
    ensurePlaylistsBuilt();
    setCurrentIndex(0);
    bumpPlaylistVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalFilter]);

  // Rebuild playlists when dislikes change so disliked artists are excluded.
  // Reads mode/currentIndex/isActive from refs to avoid stale closures
  // (dependency array intentionally only contains dislikeNames).
  const dislikesMountRef = useRef(true);
  useEffect(() => {
    if (dislikesMountRef.current) {
      dislikesMountRef.current = false;
      return;
    }
    // Read fresh values from refs — the dependency array only tracks
    // dislikeNames, so closures for mode/currentIndex/isActive would be stale.
    const curMode = modeRef.current;
    const curIndex = currentIndexRef.current;
    const active = isActiveRef.current;

    // Check BEFORE rebuilding: was the currently-playing node just disliked?
    // Use the galaxy node name (not the Deezer track name) since dislikes are
    // stored by node name and the two can differ.
    const oldPlaylist = curMode === 'shuffle' ? shuffleOrder.current : sequentialOrder.current;
    const currentNode = curIndex >= 0 && curIndex < oldPlaylist.length ? oldPlaylist[curIndex] : null;
    const currentArtistDisliked = currentNode && dislikeNames.has(currentNode.name);

    sequentialOrder.current = [];
    shuffleOrder.current = [];
    if (!active) return;
    ensurePlaylistsBuilt();
    // Adjust index so playback doesn't jump to a removed node
    const playlist = curMode === 'shuffle' ? shuffleOrder.current : sequentialOrder.current;
    const clampedIndex = Math.min(curIndex, Math.max(0, playlist.length - 1));
    if (curIndex >= playlist.length) {
      setCurrentIndex(clampedIndex);
    }
    // Skip to next preview if the currently-playing artist was disliked
    if (currentArtistDisliked && playlist.length > 0) {
      directNavRef.current = true;
      currentIndexRef.current = clampedIndex;
      navigateToIndexRef.current?.(clampedIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dislikeNames]);

  // Navigate to a node by index
  const navigateToIndex = useCallback(
    async (index) => {
      const playlist = mode === 'shuffle' ? shuffleOrder.current : sequentialOrder.current;
      if (index < 0 || index >= playlist.length) return;

      const gen = ++navigationGenRef.current;
      const node = playlist[index];

      // Select node (opens detail panel, highlights on canvas)
      navigatingFromClickRef.current = true;
      selectNode(node);
      setTimeout(() => { navigatingFromClickRef.current = false; }, 100);

      // Smoothly pan camera to follow the active node
      canvasRef.current?.followNode(node);

      // Fetch track — flush any queued background requests and use priority
      // so the track the user wants to hear isn't stuck behind prefetches
      // or detail-panel enrichment calls.
      setIsLoadingTrack(true);
      flushDeezerQueue();
      let track = trackCache.current.get(node.id);
      if (!track) {
        try {
          track = await findArtistTrack(node.name, node.id, { priority: true });
          if (track) trackCache.current.set(node.id, track);
        } catch (e) {
          console.warn('Failed to fetch track for', node.name);
        }
      }

      // Bail if a newer navigation happened while we were fetching
      if (gen !== navigationGenRef.current) return;
      setIsLoadingTrack(false);

      if (track && track.previewUrl) {
        setCurrentNodeTrack(track);
        audioPlay(track);
        skipCountRef.current = 0;

        // Pre-fetch upcoming tracks and artwork so playback feels instant.
        // Fetch a small batch (up to 3) to stay ahead of the listener
        // without hammering the API.
        const PREFETCH_AHEAD = 3;
        for (let i = 1; i <= PREFETCH_AHEAD; i++) {
          const prefetchIdx = (index + i) % playlist.length;
          const prefetchNode = playlist[prefetchIdx];
          if (prefetchNode && !trackCache.current.has(prefetchNode.id)) {
            findArtistTrack(prefetchNode.name, prefetchNode.id)
              .then((t) => {
                if (t) {
                  trackCache.current.set(prefetchNode.id, t);
                  // Cache artwork for the immediately-next track to avoid
                  // iOS Now Playing widget flicker on advance.
                  if (i === 1 && t.albumImage) prefetchArtwork(t.albumImage);
                }
              })
              .catch(() => {});
          }
        }
      } else {
        // Skip nodes without previews
        skipCountRef.current++;
        if (skipCountRef.current < MAX_CONSECUTIVE_SKIPS && playlist.length > 1) {
          const next = index + 1 < playlist.length ? index + 1 : 0;
          setCurrentIndex(next);
        } else {
          skipCountRef.current = 0;
        }
      }
    },
    [mode, selectNode, audioPlay, canvasRef, prefetchArtwork]
  );
  navigateToIndexRef.current = navigateToIndex;

  // React to currentIndex changes (fallback for state updates that don't
  // come from the direct-nav handlers, e.g. clicking a node on the canvas).
  useEffect(() => {
    if (currentIndex >= 0 && isActive) {
      if (silentSyncRef.current) {
        silentSyncRef.current = false;
        return;
      }
      if (directNavRef.current) {
        directNavRef.current = false;
        return;
      }
      navigateToIndex(currentIndex);
    }
  }, [currentIndex, isActive, navigateToIndex]);

  // Sync when user clicks a node on the canvas.
  // Only update the player's position — don't auto-play if paused.
  useEffect(() => {
    if (!selectedNode || !isActive || navigatingFromClickRef.current) return;

    const playlist = mode === 'shuffle' ? shuffleOrder.current : sequentialOrder.current;
    const idx = playlist.findIndex((n) => n.id === selectedNode.id);
    if (idx < 0 || idx === currentIndex) return;

    if (isPlaying) {
      // Player is actively playing — navigate and play the clicked node
      setCurrentIndex(idx);
    } else {
      // Player is paused — sync position silently without auto-playing
      navigationGenRef.current++;
      silentSyncRef.current = true;
      setCurrentIndex(idx);
      // Fetch track info for display only (no playback)
      const node = playlist[idx];
      const cached = trackCache.current.get(node.id);
      if (cached) {
        setCurrentNodeTrack(cached);
      } else {
        setIsLoadingTrack(true);
        findArtistTrack(node.name, node.id, { priority: true })
          .then((track) => {
            if (track) {
              trackCache.current.set(node.id, track);
              setCurrentNodeTrack(track);
            }
          })
          .catch(() => {})
          .finally(() => setIsLoadingTrack(false));
      }
    }
  }, [selectedNode, isActive, mode, currentIndex, isPlaying]);

  const handlePlay = useCallback(() => {
    if (!isActive) {
      ensurePlaylistsBuilt();
      setIsActive(true);
      setCurrentIndex(0);
      // Navigate immediately instead of waiting for the React effect cycle,
      // so the first track fetch starts without a render round-trip delay.
      directNavRef.current = true;
      navigateToIndexRef.current?.(0);
    } else {
      audioToggle();
    }
  }, [isActive, audioToggle, ensurePlaylistsBuilt]);

  const handleExit = useCallback(() => {
    audioPause();
    setIsActive(false);
    setCurrentIndex(-1);
    setCurrentNodeTrack(null);
    setIsLoadingTrack(false);
    navigationGenRef.current++;
    selectNode(null);
    canvasRef.current?.resetView();
  }, [audioPause, selectNode, canvasRef]);

  const handleModeToggle = useCallback(() => {
    setMode((prev) => {
      const newMode = prev === 'sequential' ? 'shuffle' : 'sequential';

      const allNodes = filterNodes(canvasRef.current?.getNodes() || []);
      // Re-shuffle when switching to shuffle
      if (newMode === 'shuffle') {
        shuffleOrder.current = buildShuffleOrder(allNodes);
      } else if (sequentialOrder.current.length === 0) {
        // Ensure sequential order is built
        sequentialOrder.current = buildSequentialOrder(allNodes);
      }

      // Find current node in the new ordering — don't restart the playing preview,
      // just reposition the index so subsequent next/prev use the new order.
      const currentPlaylist = prev === 'shuffle' ? shuffleOrder.current : sequentialOrder.current;
      const currentNode = currentPlaylist[currentIndex];
      if (currentNode) {
        const newPlaylist = newMode === 'shuffle' ? shuffleOrder.current : sequentialOrder.current;
        const newIdx = newPlaylist.findIndex((n) => n.id === currentNode.id);
        if (newIdx >= 0) {
          silentSyncRef.current = true;
          setCurrentIndex(newIdx);
        }
      }

      return newMode;
    });
  }, [currentIndex, canvasRef, filterNodes]);

  const playlist = getPlaylist();

  // When the user clicks the artwork / artist-name area in the player bar,
  // open the artist detail panel and highlight the node on the canvas.
  const handleArtistClick = useCallback(() => {
    if (currentIndex < 0) return;
    const pl = mode === 'shuffle' ? shuffleOrder.current : sequentialOrder.current;
    const node = pl[currentIndex];
    if (!node) return;
    // Only select if the panel isn't already showing this artist
    if (!selectedNode || selectedNode.id !== node.id) {
      selectNode(node);
      canvasRef.current?.followNode(node);
    }
  }, [currentIndex, mode, selectedNode, selectNode, canvasRef]);

  // Portal target: render the player bar into the parent view so it isn't
  // trapped inside the .galaxy-toolbar (which is position: absolute).
  const portalTarget = document.querySelector(portalSelector || '.galaxy-view');

  return (
    <>
      {!isActive ? (
        <button
          className="galaxy-player-start-button"
          onClick={handlePlay}
          title="Start Galaxy Player"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
            <polygon points="6,3 20,12 6,21" />
          </svg>
          <span>Explore</span>
        </button>
      ) : (
        <button
          className="toolbar-btn toolbar-pill toolbar-exit-explore"
          onClick={handleExit}
          title="Exit Explore"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
          <span>Exit</span>
        </button>
      )}

      {isActive && portalTarget && createPortal(
        <PreviewPlayer
          currentTrack={currentNodeTrack}
          isPlaying={isPlaying}
          isLoading={isLoadingTrack}
          progress={progress}
          onToggle={audioToggle}
          onSeek={audioSeek}
          onScrubbing={setScrubbing}
          onNext={handleNext}
          onPrev={handlePrev}
          mode={mode}
          onModeToggle={handleModeToggle}
          currentIndex={currentIndex}
          totalCount={playlist.length}
          availableGenres={availableGenres}
          selectedGenres={selectedGenres}
          onGenreSelectionChange={setSelectedGenres}
          showGenreFilter={externalMode && hasFilterableArtists && !hideRecsFilter}
          extraControls={extraControls}
          onArtistClick={handleArtistClick}
        />,
        portalTarget
      )}
    </>
  );
}

export default GalaxyPlayerController;
